import React, { useState, useEffect, useCallback } from 'react'
import botImage from 'assets/images/svg/zerone-bot-3-d-2.svg'
import styles from 'styles/components/ErrorAnimation.module.sass'
import SecondaryButton from './SecondaryButton';
import ReusableIcon from './ReusableIcon';

interface ErrorAnimationProps {
    onRetry: () => Promise<void> | void;
    message?: string;
    isVisible?: boolean;
}

/**
 * ErrorAnimation component displays an error animation with a retry option.
 * It shows a robot image, an error message with animated dots, and a retry button.
 * @component
 * @param {Object} props - The props for configuring the ErrorAnimation.
 * @param {function} props.onRetry - Callback function to invoke when the retry button is clicked.
 * @param {string} [props.message='Something went wrong, retrying...'] - Optional message to display above the retry button.
 * @param {boolean} [props.isVisible=true] - Flag to control the visibility of the error animation.
 * 
 * @example
 * <ErrorAnimation
 *  onRetry={() => fetchData()}
 *  message="Unable to load data, please try again."
 *  isVisible={true}
 * />
 * @returns {JSX.Element | null} The rendered ErrorAnimation component or null if not visible.
 */
const ErrorAnimation: React.FC<ErrorAnimationProps> = ({ onRetry, message = 'Something went wrong, retrying...', isVisible }) => {
    const [dotCount, setDotCount] = useState(0)
    const [isRetrying, setIsRetrying] = useState(false)

    useEffect(() => {
        if (!isVisible) return;

        const interval = setInterval(() => {
            setDotCount((prev) => (prev + 1) % 4)
        }, 500)

        return () => clearInterval(interval)
    }, [isVisible])

    const handleRetry = useCallback(async () => {
        setIsRetrying(true)
        try {
            await new Promise((resolve) => setTimeout(resolve, 300))
            await onRetry()
        } catch {
            console.error('Error during retry:')
        } finally {
            setIsRetrying(false)
        }
    }, [onRetry])

    if (!isVisible) return null;

    return (
        <div className={styles['error-animation']} role="alert" aria-live="polite">
            <div className={styles['error-card']}>
                <div className={styles['animated-background']} />

                <div className={styles['content-container']}>

                    <div className={styles['robot-container']}>
                        <div className={styles['robot-image-wrapper']}>
                            <img 
                                src={botImage} 
                                alt="Zerone Bot trying to fix the issue" 
                                className={styles['robot-image']}
                                onError={(e) => (e.currentTarget.style.display = 'none')}
                            />
                        </div>
                        <div className={`${styles['floating-icon']} ${styles['floating-icon-top']}`}>
                            <ReusableIcon 
                                className={`${styles['icon']} ${styles['icon-primary']}`} 
                                icon={'fluent:data-line-16-regular'} 
                                color={'#FFFFFF'} 
                            />
                        </div>
                        <div className={`${styles['floating-icon']} ${styles['floating-icon-bottom']}`}>
                            <ReusableIcon 
                                className={`${styles['icon']} ${styles['icon-secondary']}`} 
                                icon={'lineicons:gear-1'} 
                                color={'#FFFFFF'} 
                            />
                        </div>
                    </div>

                    <div className={styles['message-container']}>
                        <h3 className={styles['error-title']}>Oops! Data Connection Lost</h3>
                        <p className={styles['error-message']}>
                            {message}
                            {'.'.repeat(dotCount)}
                        </p>
                    </div>

                    <div className={styles['progress-indicators']}>
                        {[0, 1, 2].map((index) => (
                            <div key={index} className={styles['progress-dot']} />
                        ))}
                    </div>

                    <SecondaryButton 
                        text={isRetrying ? 'Retrying...' : 'Retry Connection'} 
                        primaryColor={'transparent'} 
                        textColor={'#FF5522'} 
                        hoverColor={'#FF5522'} 
                        hoverTextColor={'#FFFFFF'}
                        icon={'material-symbols:refresh-rounded'}
                        doSomething={() => { handleRetry(); }}          
                    />

                    <div className={styles['decorative-circle-large']} />
                    <div className={styles['decorative-circle-small']} />
                </div>
            </div>
        </div>
    )
}

export default ErrorAnimation